import * as React from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react';
import { DayPicker, NavProps } from 'react-day-picker';
import { cn } from '@/utils/utils';
import { format } from 'date-fns';
import { buttonVariants } from '../common/Button';
import { useTranslation } from 'react-i18next';
import { getLocaleDateFns } from '@/utils/language';
import { enUS, ja } from 'date-fns/locale';
import { LANGUAGES_SUPPORTED } from '@/types/enums';

export type ReactDayPickerProps = React.ComponentProps<typeof DayPicker>;

function ReactDayPicker2({
    className,
    classNames,
    showOutsideDays = true,
    captionLayout = 'dropdown',
    ...props
}: ReactDayPickerProps) {
    // hooks
    const { t, i18n: { language } } = useTranslation();
    const localeDateFns = getLocaleDateFns(language);

    const navigationButtonStyles = cn(
        buttonVariants({ variant: 'outlineForeground' }),
        'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100'
    );

    return (
        <DayPicker
            showOutsideDays={showOutsideDays}
            className={cn('md:p-3 rounded-md border', className)}
            classNames={{
                nav: 'flex items-center justify-between',
                chevron: 'w-4 h-4',
                button_next: navigationButtonStyles,
                button_previous: navigationButtonStyles,
                caption_label: cn('text-sm font-medium', captionLayout !== 'label' && 'hidden'),
                day: cn(
                    buttonVariants({ variant: 'ghost' }),
                    'h-14 w-full max-w-20 rounded-none md:rounded-base md:mx-1 font-normal text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20 hover:cursor-pointer'
                ),
                day_button: 'h-full w-full',
                selected: `!bg-[#FFEBEB] text-black hover:bg-[#FFEBEB] hover:text-black focus:bg-[#FFEBEB] focus:text-black ${language === LANGUAGES_SUPPORTED.Japanese ? 'after:content-["不可"]' : 'after:content-["OFF"]'} day-selected-custom`,
                disabled: 'text-muted-foreground opacity-50',
                focused: '',
                hidden: 'invisible',
                outside: 'day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30',
                today: 'bg-accent text-accent-foreground',
                dropdown: 'border-none outline-none hover:cursor-pointer',
                dropdown_root: '',
                dropdowns: 'flex flex-row-reverse gap-2 w-[100px] items-center justify-center',
                footer: '',
                month: 'w-full border-collapse',
                month_caption: 'absolute top-1 left-1/2 -translate-x-1/2 flex items-center',
                month_grid: 'w-full',
                months: 'relative flex flex-col',
                months_dropdown: '',
                week: 'flex w-full mt-2 justify-between',
                week_number: '',
                week_number_header: '',
                weekday: 'text-calendar-1 rounded-md w-9 font-medium w-full max-w-20 text-black',
                weekdays: 'flex h-10 items-center border-y border-border-3 justify-between mt-2.5',
                weeks: '',
                years_dropdown: '',
                ...classNames
            }}
            captionLayout={captionLayout}
            components={{
                Chevron: ({ className, orientation = 'down' }) => {
                    const iconChevron = {
                        'down': ChevronDown,
                        'up': ChevronUp,
                        'left': ChevronLeft,
                        'right': ChevronRight
                    };

                    const Comp = iconChevron[orientation];

                    return (
                        <Comp className={className} />
                    );
                },
                Nav: ({ children, className, ...props }: NavProps) => {
                    const [nextButton, previousButton] = React.Children.map(children, child => child) as Array<React.ReactElement<any, string | React.JSXElementConstructor<any>>>;

                    return (
                        <nav className={cn('flex items-center justify-between', className)} {...props}>
                            {nextButton}
                            {previousButton}
                        </nav>
                    );
                },
                Day: (props) => {
                    const { day, modifiers, ...tdProps } = props;
                    return (
                        <td
                            className={`relative ${className} ${modifiers.selected ? 'bg-selected text-black' : ''}`}
                            {...tdProps}
                        >

                            {/* Hiển thị nhãn "OFF" khi ngày được chọn */}
                            {modifiers.selected && (
                                <span className='absolute top-1 right-1 bg-[#FFBF34] text-black text-xs font-bold px-[4px] py-[2px] rounded'>
                                    {t('calendar.off')}
                                </span>
                            )}
                        </td>
                    );
                }
            }}
            locale={localeDateFns}
            formatters={{
                formatMonthDropdown: (month) => {
                    const formatMonth = 'MMM';
                    return format(new Date().setMonth(month), formatMonth, {
                        locale: localeDateFns
                    });
                },
                formatCaption: (date) => {
                    const locale = language === LANGUAGES_SUPPORTED.Japanese ? ja : enUS;
                    return format(date, 'MMMM yyyy', { locale });
                }
            }}
            endMonth={new Date((new Date()).getFullYear() + 30, 11, 31)}
            {...props}
        />
    );
}

ReactDayPicker2.displayName = 'ReactDayPicker2';

export { ReactDayPicker2 };
